import { useEffect, useMemo, useRef } from "react"

import { type LayoutChangeEvent, Platform, View } from "react-native"
import { Gesture, GestureDetector } from "react-native-gesture-handler"

import { type GraphAxisLabel, nearestGraphAxisValue } from "wealthmode-frontend/components"

import { overlayDataX } from "./graphHitMath"

export function GraphHitOverlay({
  xAxis,
  xMin,
  xMax,
  onCursorChange,
  className,
}: {
  xAxis: GraphAxisLabel[]
  xMin: number
  xMax: number
  onCursorChange: (value: number) => void
  className?: string
}) {
  const overlayRef = useRef<React.ComponentRef<typeof View> | null>(null)
  const overlayWidthRef = useRef(0)
  const onCursorChangeRef = useRef(onCursorChange)
  onCursorChangeRef.current = onCursorChange
  const lastEmittedCursorValueRef = useRef<number | null>(null)
  const pendingLocationXRef = useRef<number | null>(null)
  const rafIdRef = useRef<number | null>(null)

  function handleOverlayLayout(event: LayoutChangeEvent) {
    const { width } = event.nativeEvent.layout
    if (width > 0) {
      overlayWidthRef.current = width
    }
  }

  useEffect(() => {
    if (Platform.OS !== "web") {
      return
    }

    const el = overlayRef.current as unknown as Element | null
    if (el == null || typeof ResizeObserver === "undefined") {
      return
    }

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width } = entry.contentRect
        if (width > 0) {
          overlayWidthRef.current = width
        }
      }
    })
    observer.observe(el)

    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    return () => {
      if (rafIdRef.current != null) {
        cancelAnimationFrame(rafIdRef.current)
        rafIdRef.current = null
      }
    }
  }, [])

  const panGesture = useMemo(() => {
    const snapFromLocationX = (locationX: number) => {
      const overlayWidth = overlayWidthRef.current
      if (overlayWidth <= 0 || xAxis.length === 0) {
        return
      }

      const dataX = overlayDataX(locationX, overlayWidth, xMin, xMax)
      const snapped = nearestGraphAxisValue(dataX, xAxis)
      if (snapped === lastEmittedCursorValueRef.current) {
        return
      }

      lastEmittedCursorValueRef.current = snapped
      onCursorChangeRef.current(snapped)
    }

    const flush = () => {
      rafIdRef.current = null
      const locationX = pendingLocationXRef.current
      pendingLocationXRef.current = null
      if (locationX == null) {
        return
      }

      snapFromLocationX(locationX)
    }

    const snap = (locationX: number) => {
      pendingLocationXRef.current = locationX
      if (rafIdRef.current != null) {
        return
      }

      rafIdRef.current = requestAnimationFrame(flush)
    }

    return Gesture.Pan()
      .runOnJS(true)
      .minDistance(0)
      .onBegin((event) => snap(event.x))
      .onUpdate((event) => snap(event.x))
  }, [xAxis, xMax, xMin])

  return (
    <GestureDetector gesture={panGesture}>
      <View
        ref={overlayRef}
        className={className}
        onLayout={handleOverlayLayout}
        pointerEvents="box-only"
        style={
          Platform.OS === "web"
            ? ({
                touchAction: "none",
                userSelect: "none",
                WebkitUserSelect: "none",
                cursor: "pointer",
              } as React.ComponentProps<typeof View>["style"])
            : undefined
        }
      />
    </GestureDetector>
  )
}
